import { useState, useImperativeHandle, forwardRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { projectMemberService, type ProjectMember } from '@/services/projectMemberService'

interface ChangeProjectMemberRoleModalProps {
  canDirectChange: boolean // platform owner or project owner
  canRequestChange: boolean // platform owner or project owner or project admin
  onSuccess?: () => void
}

export interface ChangeProjectMemberRoleModalRef {
  open: (member: ProjectMember) => void
  close: () => void
}

const ChangeProjectMemberRoleModal = forwardRef<ChangeProjectMemberRoleModalRef, ChangeProjectMemberRoleModalProps>(
  ({ canDirectChange, canRequestChange, onSuccess }, ref) => {
    const { t } = useTranslation('projectMembers')
    const { projectId } = useParams<{ projectId: string }>()
    const [isOpen, setIsOpen] = useState(false)
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')
    const [success, setSuccess] = useState(false)
    const [member, setMember] = useState<ProjectMember | null>(null)
    const [selectedRole, setSelectedRole] = useState<'owner' | 'admin' | 'member' | 'viewer' | 'observer'>('member')
    const [actionType, setActionType] = useState<'direct' | 'request'>('direct')

    useImperativeHandle(ref, () => ({
      open: (memberData: ProjectMember) => {
        setMember(memberData)
        setSelectedRole(memberData.role)
        // If can direct change, default to direct; otherwise request
        setActionType(canDirectChange ? 'direct' : 'request')
        setIsOpen(true)
        setError('')
        setSuccess(false)
      },
      close: () => {
        setIsOpen(false)
        setMember(null)
        setSelectedRole('member')
        setActionType('direct')
        setError('')
        setSuccess(false)
      },
    }))

    const handleSubmit = async () => {
      if (!member || !projectId || loading) return

      try {
        setLoading(true)
        setError('')

        if (actionType === 'direct') {
          // Direct change role
          await projectMemberService.updateMemberRole(projectId, member.user_id, selectedRole)
        } else {
          // Submit request
          await projectMemberService.requestMemberRoleChange(projectId, member.user_id, selectedRole)
        }

        // Show success message instead of closing immediately
        setSuccess(true)
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'errors.operationFailed'
        if (errorMessage.startsWith('errors.')) {
          setError(t(errorMessage, errorMessage))
        } else {
          setError(errorMessage)
        }
      } finally {
        setLoading(false)
      }
    }

    const handleClose = () => {
      const wasSuccess = success
      setIsOpen(false)
      setMember(null)
      setSelectedRole('member')
      setActionType('direct')
      setError('')
      setSuccess(false)

      // Only call onSuccess if the operation was successful
      if (wasSuccess) {
        onSuccess?.()
      }
    }

    const getRoleColor = (role: string) => {
      switch (role) {
        case 'owner':
          return 'bg-purple-100 text-purple-800 border-purple-200'
        case 'admin':
          return 'bg-indigo-100 text-indigo-800 border-indigo-200'
        case 'member':
          return 'bg-blue-100 text-blue-800 border-blue-200'
        case 'viewer':
          return 'bg-green-100 text-green-800 border-green-200'
        case 'observer':
          return 'bg-gray-100 text-gray-800 border-gray-200'
        default:
          return 'bg-gray-100 text-gray-800 border-gray-200'
      }
    }

    if (!isOpen || !member) return null

    const canSubmit = selectedRole !== member.role && !loading

    return (
      <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white/30 backdrop-blur-md">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
          {/* Header */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-purple-600">
                  更改成員角色
                </h2>
                <p className="text-sm text-gray-600 mt-1">
                  修改專案成員權限
                </p>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                disabled={loading}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-6 space-y-4">
            {/* Success Message */}
            {success && (
              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-green-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-green-900 mb-1">
                      {actionType === 'direct'
                        ? '角色更改成功！'
                        : '角色更改請求已提交！'}
                    </p>
                    <p className="text-xs text-green-700">
                      {actionType === 'direct'
                        ? '成員角色已立即更新。'
                        : '請求正在等待批准。'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Error Message */}
            {error && !success && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
                <svg className="w-5 h-5 text-red-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}

            {/* Member Info */}
            {!success && (
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm text-gray-600 mb-3">
                  更改以下成員的角色：
                </p>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">帳號:</span>
                    <span className="text-sm text-gray-900">{member.user.account}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">名稱:</span>
                    <span className="text-sm text-gray-900">{member.user.name}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-gray-700">目前角色:</span>
                    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${getRoleColor(member.role)}`}>
                      {t(`projectRoles.${member.role}`, member.role)}
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Action Type Selection (Only if can do both) */}
            {!success && canDirectChange && canRequestChange && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  操作類型
                </label>
                <div className="flex gap-3">
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="direct"
                      checked={actionType === 'direct'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'direct'
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        直接更改
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        立即套用角色變更
                      </p>
                    </div>
                  </label>
                  <label className="flex-1 cursor-pointer">
                    <input
                      type="radio"
                      name="actionType"
                      value="request"
                      checked={actionType === 'request'}
                      onChange={(e) => setActionType(e.target.value as 'direct' | 'request')}
                      disabled={loading}
                      className="sr-only"
                    />
                    <div className={`px-4 py-3 border-2 rounded-lg transition-all ${
                      actionType === 'request'
                        ? 'border-purple-600 bg-purple-50'
                        : 'border-gray-300 hover:border-gray-400'
                    }`}>
                      <span className="text-sm font-medium text-gray-900 block">
                        提交請求
                      </span>
                      <p className="text-xs text-gray-600 mt-1">
                        創建角色變更請求
                      </p>
                    </div>
                  </label>
                </div>
              </div>
            )}

            {/* Admin/Non-owner Info Notice */}
            {!success && !canDirectChange && canRequestChange && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <svg className="w-5 h-5 text-blue-600 mt-0.5 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                  <div className="flex-1">
                    <p className="text-sm text-blue-700">
                      您可以提交角色變更請求，請求需要專案擁有者批准。
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Role Selection */}
            {!success && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  新角色
                </label>
                <select
                  value={selectedRole}
                  onChange={(e) => setSelectedRole(e.target.value as any)}
                  disabled={loading}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50"
                >
                  <option value="owner">{t('projectRoles.owner', 'Owner')}</option>
                  <option value="admin">{t('projectRoles.admin', 'Admin')}</option>
                  <option value="member">{t('projectRoles.member', 'Member')}</option>
                  <option value="viewer">{t('projectRoles.viewer', 'Viewer')}</option>
                  <option value="observer">{t('projectRoles.observer', 'Observer')}</option>
                </select>
              </div>
            )}
          </div>

          {/* Actions */}
          <div className="p-6 border-t border-gray-200 flex items-center justify-end gap-3">
            {success ? (
              <button
                type="button"
                onClick={handleClose}
                className="px-5 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
              >
                關閉
              </button>
            ) : (
              <>
                <button
                  type="button"
                  onClick={handleClose}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={loading}
                >
                  取消
                </button>
                <button
                  type="button"
                  onClick={handleSubmit}
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                  disabled={!canSubmit}
                >
                  {loading && (
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  )}
                  <span>
                    {loading
                      ? '處理中'
                      : actionType === 'direct'
                        ? '更改角色'
                        : '提交請求'}
                  </span>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    )
  }
)

ChangeProjectMemberRoleModal.displayName = 'ChangeProjectMemberRoleModal'

export default ChangeProjectMemberRoleModal
